import { useEffect, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import type { FieldDefinition } from '@/types/cgt'
import { sameOosValue } from '@/lib/oos'
import type { ResolvedLabelLayout } from '@/lib/fieldLayout'
import { FieldRow } from './FieldRow'
import { useOosGuard } from './useOosGuard'

interface Props {
  field: FieldDefinition
  value: unknown
  locked: boolean
  error: string | undefined
  submissionId: string
  preview: boolean
  onLiveChange: (fieldId: string, value: unknown) => void
  /** Saves and throws on failure, so a refused save keeps the prompt open. */
  onSave: (fieldId: string, value: unknown, comment?: string) => Promise<void>
  layout: ResolvedLabelLayout
  stacked: boolean
}

/**
 * A number field with a specification (z8nrz7eq4e). Shows the severity flag for
 * the saved value, asks for a comment before saving a value in a band that
 * requires one, and lets an alerted group member review the alert. The flag,
 * prompt and review live in useOosGuard, shared with the other field types.
 */
export function OosNumberField({
  field,
  value,
  locked,
  error,
  submissionId,
  preview,
  onLiveChange,
  onSave,
  layout,
  stacked,
}: Props) {
  const [localValue, setLocalValue] = useState(value !== undefined && value !== null ? String(value) : '')
  const guard = useOosGuard({
    spec: field.oos,
    fieldType: 'number',
    fieldId: field.id,
    savedValue: value,
    submissionId,
    preview,
    locked,
    onSave: (v, comment) => onSave(field.id, v, comment),
  })

  // Follows the saved value when it changes from outside (another save, a
  // reload). Not on a save finishing: after a refused save the typed value
  // must stay in the box, unsaved, next to its error.
  useEffect(() => {
    if (guard.savingRef.current) return
    setLocalValue(value !== undefined && value !== null ? String(value) : '')
    // `guard.savingRef` is a stable ref.
  }, [value])

  const showBorder = guard.borderColor && sameOosValue('number', localValue, guard.savedValue)

  return (
    <div>
      <FieldRow
        label={field.label}
        required={field.required}
        helpText={field.helpText}
        layout={layout}
        stacked={stacked}
        error={guard.saveError ?? error}
        controlClassName='flex flex-1 items-center gap-2'
      >
        {({ id, describedBy }) => (
          <>
            <Input
              id={id}
              aria-describedby={describedBy}
              type='number'
              value={localValue}
              disabled={locked}
              placeholder={field.helpText}
              className={cn('flex-1', locked && 'opacity-50 cursor-not-allowed bg-gray-50')}
              style={showBorder ? { borderColor: guard.borderColor } : undefined}
              onChange={e => {
                setLocalValue(e.target.value)
                guard.clearError()
                onLiveChange(field.id, e.target.value)
              }}
              onBlur={() => guard.guardSave(localValue)}
            />
            {guard.flag}
            {guard.saving && <Loader2 className='h-3 w-3 animate-spin text-muted-foreground' />}
            {guard.showSaved && (
              <span className='inline-flex items-center gap-1 text-xs text-green-600 font-medium animate-in fade-in'>
                <Check className='h-3 w-3' /> Saved
              </span>
            )}
          </>
        )}
      </FieldRow>
      {guard.dialog}
    </div>
  )
}
